import { LEGO_CONSTANTS } from 'constants/index';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const applyClassicFontStyles = root => {
  root.style.setProperty('font-family', LEGO_CONSTANTS.CLASSIC_FONT_FAMILY);
  root.style.setProperty('--fw-font-family', LEGO_CONSTANTS.CLASSIC_FONT_FAMILY);
};

const removeClassicFontStyles = root => {
  root.style.removeProperty('font-family', LEGO_CONSTANTS.CLASSIC_FONT_FAMILY);
  root.style.removeProperty('--fw-font-family', LEGO_CONSTANTS.CLASSIC_FONT_FAMILY);
};

const applyDewFontStyles = root => {
  root.style.setProperty('font-family', LEGO_CONSTANTS.DEW_FONT_FAMILY);
  root.style.setProperty('--fw-font-family', LEGO_CONSTANTS.DEW_FONT_FAMILY);
};

const removeDewFontStyles = root => {
  root.style.removeProperty('font-family', LEGO_CONSTANTS.DEW_FONT_FAMILY);
  root.style.removeProperty('--fw-font-family', LEGO_CONSTANTS.DEW_FONT_FAMILY);
};

const getThemeFromParentURL = () => {
  try {
    const parentURL = new URL(window.location.href);
    const mode = parentURL.searchParams.get('mode');
    const theme = parentURL.searchParams.get('theme');

    if (mode === 'dew') {
      return {
        mode: 'dew',
        theme: theme || 'system',
        isDewMode: true,
      };
    }

    return {
      isDewMode: false,
    };
  } catch (error) {
    console.info('Cannot access parent URL for theme detection:', error);
    return { isDewMode: false };
  }
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(null);
  const [currentThemeClass, setCurrentThemeClass] = useState('');

  const applyTheme = useCallback(
    root => {
      root.classList.remove('dew-light-theme', 'dew-dark-theme', 'classic-theme');

      if (themeConfig.isDewMode) {
        const { theme } = themeConfig;

        let isDarkMode;
        if (theme === 'system') {
          isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        } else {
          isDarkMode = theme === 'dark';
        }

        const themeClass = isDarkMode ? 'dew-dark-theme' : 'dew-light-theme';
        removeClassicFontStyles(root);
        applyDewFontStyles(root);
        root.classList.add(themeClass);
        setCurrentThemeClass(themeClass);

        return;
      }
      removeDewFontStyles(root);
      applyClassicFontStyles(root);
      root.classList.add('classic-theme');
      setCurrentThemeClass('classic-theme');
    },
    [themeConfig]
  );

  useEffect(() => {
    const config = getThemeFromParentURL();
    setThemeConfig(config);
  }, []);

  useEffect(() => {
    if (!themeConfig) return;
    const root = document.documentElement;

    applyTheme(root);

    let mediaQuery;
    let listener;

    if (themeConfig.isDewMode && themeConfig.theme === 'system') {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      listener = () => applyTheme();
      mediaQuery.addEventListener('change', listener);
    }

    return () => {
      if (mediaQuery && listener) {
        mediaQuery.removeEventListener('change', listener);
      }
    };
  }, [themeConfig, applyTheme]);

  const value = {
    themeConfig,
    isDewMode: themeConfig?.isDewMode || false,
    theme: themeConfig?.theme,
    mode: themeConfig?.mode,
    currentThemeClass,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Keep the original ThemeWrapper for backward compatibility
const ThemeWrapper = ({ children }) => {
  return <ThemeProvider>{children}</ThemeProvider>;
};

export default ThemeWrapper;
